import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {
    LoginButton,
    roleToUserRole,
    type LoginTypeData,
    type Role,
} from "@khan/auth-shared";
import {useMutation} from "@khan/data-gql";
import {redirect} from "@khan/urls";

import type {GetOneTimeUseMicrosoftLoginUrlMutationType} from "./__graphql-types__/index.ts";
import {GET_MICROSOFT_LOGIN_URL_MUTATION} from "./microsoft-graphql.ts";
import {MicrosoftIcon} from "./microsoft-icon.tsx";

export interface CompleteLoginOverrideParamsMicrosoft {
    userData: LoginTypeData;
}

interface Props {
    disabled?: boolean;
    style?: StyleType;
    size?: "small" | "medium" | "large";
    continueUrl?: string;
    role?: Role;
    isSignup?: boolean;
    /**
     * Callback for when the login fails.
     *
     * This can be caused due to Sign in with Apple auth errors or errors
     * caused in the the Sign in with Apple GraphQL mutation.
     */
    onError?: (errorMessage: string) => unknown;
    /**
     * Event Type for the CEDAR event which will be published on click
     *
     * We only pass the event type and rely on this component to determine
     * the rest of the event data.
     *
     * NOTE: If you update the list of possible values for this type, make sure
     * to update `handleClick` to publish the correct event.
     */
    cedarEventType?: "SignupMicrosoftButtonClicked";
    iconOnly?: boolean;
    /**
     * Whether form validation is required before executing the login logic
     */
    validationRequired?: boolean;
    /**
     * Whether the current form is valid. Determined by the TOS checkbox
     */
    isFormValid?: boolean;
    /**
     * Called when the form is invalid and validation is required.
     */
    onValidationError?: () => void;
}

export function MicrosoftButton(props: Props): React.ReactNode {
    const {
        cedarEventType,
        continueUrl,
        isSignup,
        onError,
        role,
        validationRequired,
        isFormValid,
        onValidationError,
    } = props;
    const [getMicrosoftLoginUrl] = useMutation(
        GET_MICROSOFT_LOGIN_URL_MUTATION,
    );
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const handleClick = React.useCallback(async () => {
        if (validationRequired && !isFormValid) {
            // Trigger error callback for invalid form when validation is required
            onValidationError?.();
            return;
        }

        if (cedarEventType === "SignupMicrosoftButtonClicked") {
            publishAnalyticsEvent({
                eventType: "SignupMicrosoftButtonClicked",
                eventSchemaVersion: 1,
            });
        }

        const variables: GetOneTimeUseMicrosoftLoginUrlMutationType["variables"] =
            {
                action: isSignup ? "SIGNUP" : "LOGIN",
                continueUrl,
                role: roleToUserRole(role),
            };

        const result = await getMicrosoftLoginUrl(variables);
        if (
            !result ||
            result.status !== "success" ||
            result.data.getOneTimeUseMicrosoftLoginUrl?.error
        ) {
            if (onError) {
                onError("Could not get login URL from Microsoft.");
            }
            return;
        }
        if (result.data.getOneTimeUseMicrosoftLoginUrl?.redirectUrl) {
            redirect(result.data.getOneTimeUseMicrosoftLoginUrl?.redirectUrl);
        }
    }, [
        cedarEventType,
        continueUrl,
        getMicrosoftLoginUrl,
        isSignup,
        onError,
        publishAnalyticsEvent,
        role,
        validationRequired,
        isFormValid,
        onValidationError,
    ]);

    return (
        <LoginButton
            title={t`Continue with Microsoft`}
            iconComponent={MicrosoftIcon}
            disabled={props.disabled}
            onClick={handleClick}
            style={props.style}
            size={props.size}
            iconOnly={props.iconOnly}
        />
    );
}
